import { Link } from "react-router-dom";
import * as Icons from "heroicons-react";
import Header from "../../Common/Header/Header";
import SideBar from "../../Common/SideBar/SideBar";
import { signInUser } from "../../Utility/Auth";
import { useSelector } from "react-redux";
import ReportSideBar from "../../Common/SideBar/ReportSideBar";
import DatePicker from "react-datepicker";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import WareHouseDW from "../../Common/CustomSelect/WareHouseDW";
import { useWarehouseQuery } from "../../../services/warehouseApi";
import { useSaleReturnWiseQuery } from "../../../services/reportsApi";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const DateWiseSalesReturnReport = () => {
  const auth = signInUser();
  const [warehouse, setWarehouse] = useState("allWh");

  const [startDate, setStartDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const [endDate, setEndDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const { aamarId, storeSettings } = auth;
  const lang = useSelector((state) => state.languageReducer);

  const {
    data: sale,
    isLoading,
    isError,
    error,
  } = useSaleReturnWiseQuery({
    startDate,
    endDate,
    warehouse,
    aamarId,
  });

  // console.log("sale", sale);

  // fine warehouse Name
  const [whName, setWhName] = useState("All Warehouses");

  const { data: wh, refetch } = useWarehouseQuery(warehouse);

  // Call refetch only if user?.warehouse changes
  useEffect(() => {
    if (warehouse) {
      refetch();
    }
  }, [warehouse, refetch]);

  // Update warehouse name when data changes
  useEffect(() => {
    if (wh?.name) {
      setWhName(wh.name); // Directly set the name, no need for optional chaining here
    }
  }, [wh]);

  // Export CSV
  const handleExportCSV = () => {
    if (!sale?.length) {
      alert("No data available for export.");
      return;
    }

    const start = new Date(startDate).toLocaleString();
    const end = new Date(endDate).toLocaleString();
    const generatedOn = new Date().toLocaleString();
    const generatedBy = auth?.name || "Aamar Dokan";

    const warehouse = whName || "All Warehouses";
    const storeName = auth?.storeSettings?.storeName?.toUpperCase() || "Aamar Dokan";
    const reportTitle = "Date Wise Sales Return Report";

    const headerInfo = [
      `,Store: ${storeName},,,,,Warehouse: ${warehouse}`,
      `,Report: ${reportTitle},,,,,Date Range: ${start} to ${end}`,
      `,Generated On: ${generatedOn},,,,Generated By: ${generatedBy}`,
      "",
      "",
    ];

    const csvTableHeader = [
      "S/N",
      "Invoice",
      "Date",
      "Customer",
      "Phone",
      "Warehouse",
      "Total Items",
      "Total",
      "Gross Total",
    ].join(",");

    let totalItems = 0;
    let totalGross = 0;
    let totalAmount = 0;

    const csvRows = sale.map((item, index) => {
      const items = Number(item?.totalItem || 0);
      const gross = Number(item?.grossTotal || 0);
      const total = Number(item?.total || 0);

      totalItems += items;
      totalGross += gross;
      totalAmount += total;

      return [
        index + 1,
        item?.invoiceId || "",
        new Date(item?.date).toLocaleDateString(),
        item?.customer || "Walkway Customer",
        item?.phone || "017 XX XXX XXX",
        item?.warehouse || "",
        items,
        total,
        gross,
      ].join(",");
    });

    const summaryLabelRow = [
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "** TOTAL SUMMARY **",
    ].join(",");

    const summaryHeaderRow = [
      "",
      "",
      "",
      "",
      "",
      "",
      "Total Items",
      "Total",
      "Gross Total",
    ].join(",");

    const summaryDataRow = [
      "",
      "",
      "",
      "",
      "",
      "",
      totalItems,
      totalAmount,
      totalGross,
    ].join(",");

    const csvContent = [
      ...headerInfo,
      csvTableHeader,
      ...csvRows,
      "",
      "",
      summaryLabelRow,
      summaryHeaderRow,
      summaryDataRow,
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "DateWiseSalesReturnReport.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // export PDF
  const handleExportPDF = () => {
    const doc = new jsPDF("landscape");
    if (!sale?.length) {
      alert("No data available for export.");
      return;
    }

    // Top Header value
    const options = { month: "numeric", day: "numeric", year: "numeric" };
    const formattedStart = new Date(startDate).toLocaleDateString(
      "en-US",
      options
    );
    const formattedEnd = new Date(endDate).toLocaleDateString("en-US", options);
    const reportGeneratedAt = new Date().toLocaleString();
    const storeName =
      auth?.storeSettings?.storeName?.toUpperCase() || "Aamar Dokan";
    const warehouseName = whName;
    const reportName = "Date Wise Sales Return Report";

    // Title
    doc.setFontSize(16);
    doc.text(storeName, 140, 15, { align: "center" });

    doc.setFontSize(12);
    doc.text(reportName, 140, 22, { align: "center" });

    // Header info - Justify between
    doc.setFontSize(10);
    doc.text(`Store: ${storeName}`, 14, 28);
    doc.text(`Warehouse: ${warehouseName}`, 14, 33);
    doc.text(`Date: ${formattedStart} - ${formattedEnd}`, 14, 39);
    doc.text(`Generated By: ${auth?.name}`, 280, 28, { align: "right" });
    doc.text(`${reportGeneratedAt}`, 280, 33, { align: "right" });

    // === Sales Table Headers ===
    const headers = [
      [
        "S/N",
        "Invoice",
        "Date",
        "Customer",
        "Phone",
        "Warehouse",
        "Total Items",
        "Total",
        "Gross Total",
      ],
    ];

    const body = sale.map((item, index) => [
      index + 1,
      item.invoiceId,
      new Date(item.date).toLocaleDateString(),
      item.customer || "Walkway Customer",
      item.phone || "017 XX XXX XXX",
      item.warehouse,
      item.totalItem,
      item.total,
      item.grossTotal,
    ]);

    // === Sales Table ===
    autoTable(doc, {
      head: headers,
      body: body,
      startY: 45,
      theme: "grid",
      styles: {
        fontSize: 9,
        halign: "center",
      },
      headStyles: {
        fillColor: [242, 101, 45],
        textColor: [255, 255, 255],
        fontStyle: "bold",
      },
    });

    // === Summary Section ===
    const finalY = doc.lastAutoTable.finalY + 10;
    const boxWidth = 80;
    const pageWidth = doc.internal.pageSize.getWidth();
    const boxX = pageWidth - boxWidth - 15;
    const boxY = finalY;

    doc.setLineDashPattern([1, 1], 0);
    doc.rect(boxX, boxY, boxWidth, 30);
    doc.setLineDashPattern([], 0);

    const labelX = boxX + 5;
    const valueX = boxX + boxWidth - 5;
    let y = boxY + 10;

    doc.setFontSize(9);

    doc.text("Gross Total:", labelX, y);
    doc.text(`${sumField(sale, "grossTotal")}`, valueX, y, { align: "right" });

    y += 6;
    doc.text("Total Items:", labelX, y);
    doc.text(`${sumField(sale, "totalItem")}`, valueX, y, { align: "right" });

    y += 6;
    doc.text("Total Return:", labelX, y);
    doc.text(`${sumField(sale, "total")}`, valueX, y, { align: "right" });

    // Save the PDF
    doc.save("DateWiseSalesReturnReport.pdf");
  };

  // === Utility Function ===
  const sumField = (data, field) =>
    data
      .reduce((acc, item) => acc + (parseFloat(item[field]) || 0), 0)
      .toFixed(2);

  //warehouse
  const handleOnchangeWareHouseFrom = (e) => {
    if (e.option !== "no-warehouse") {
      setWarehouse(e.option);
    } else {
      setWarehouse("allWh");
    }
  };

  return (
    <div>
      <div>
        <h2 className="mb-3">
          <Icons.ShoppingCartOutline size={35} /> Date Wise Sales Return
        </h2>
      </div>

      <div
        className="mt-3 w-40 p-3"
        style={{ backgroundColor: "#f1f3f5", borderRadius: "8px" }}
      >
        <div className=" d-flex mt-2 mb-2 align-items-center justify-content-center gap-2">
          <DatePicker
            selected={new Date(startDate)}
            className="form-control w-100"
            onChange={(date) =>
              setStartDate(format(new Date(date), "MM-dd-yyyy"))
            }
          />
          <DatePicker
            selected={new Date(endDate)}
            className="form-control me-4"
            onChange={(date) =>
              setEndDate(format(new Date(date), "MM-dd-yyyy"))
            }
          />
        </div>

        <div className="">
          {auth?.type === "admin" && (
            <div className="my-3">
              <WareHouseDW
                id="warehouse"
                name="warehouse"
                handleOnChange={handleOnchangeWareHouseFrom}
                className="form-control"
              />
            </div>
          )}
        </div>

        <div className="d-flex gap-3 justify-content-center align-items-center text-center">
          {/* Download CSV Button */}
          <button
            className="btn btn-dark w-100 mt-3"
            style={{ borderRadius: "5px" }}
            onClick={handleExportCSV}
          >
            <Icons.DownloadOutline size={22} /> Download CSV
          </button>

          {/* Download PDF Button */}
          <button
            className="btn btn-dark w-100 mt-3"
            style={{ borderRadius: "5px" }}
            onClick={handleExportPDF}
          >
            <Icons.DocumentOutline size={22} /> Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateWiseSalesReturnReport;
